import type { ChartContext } from "./context";

export function buildHeatmapOption(
  context: ChartContext,
): Record<string, unknown> {
  const { data, isDark, translateNode, unitText, palette, ct, base } = context;
  const ds = data.datasets[0];
  if (!ds) return base;
  const vals = ds.values as unknown as Array<Record<string, unknown>>;
  const heatXLabels = [...new Set(vals.map((v) => v.x as string))];
  const heatYLabels = [...new Set(vals.map((v) => v.y as string))];
  const xMap = new Map(heatXLabels.map((n, idx) => [n, idx]));
  const yMap = new Map(heatYLabels.map((n, idx) => [n, idx]));
  const minV = Math.min(...vals.map((v) => v.value as number));
  const maxV = Math.max(...vals.map((v) => v.value as number));
  const heatFormatter = (p: { data: number[] }) => {
    const d = p.data;
    return (
      translateNode(heatXLabels[d[0]]) +
      " ~ " +
      translateNode(heatYLabels[d[1]]) +
      `\n${translateNode("Value")}: ` +
      unitText(d[2])
    );
  };
  return {
    ...base,
    tooltip: {
      position: "top",
      backgroundColor: ct.bgElevated,
      borderColor: ct.border,
      formatter: heatFormatter,
    },
    xAxis: {
      type: "category",
      data: heatXLabels,
      position: "top",
      axisLabel: { color: ct.secondary, formatter: translateNode },
    },
    yAxis: {
      type: "category",
      data: heatYLabels,
      axisLabel: { color: ct.secondary, formatter: translateNode },
    },
    grid: { left: 60, right: 20, bottom: 40, top: 65, containLabel: true },
    visualMap: {
      min: minV,
      max: maxV,
      calculable: true,
      orient: "horizontal",
      left: "center",
      bottom: 0,
      textStyle: { color: ct.secondary },
      inRange: {
        color: [
          isDark ? "#172b1d" : "#e4ede6",
          isDark ? "#21995a" : "#43a86b",
          palette[0],
        ],
      },
    },
    series: [
      {
        type: "heatmap",
        data: vals.map(
          (v) =>
            [
              xMap.get(v.x as string)!,
              yMap.get(v.y as string)!,
              v.value as number,
            ] as number[],
        ),
        label: { show: true, color: ct.color },
        emphasis: { itemStyle: { shadowBlur: 10 } },
      },
    ],
  };
}
